import type { LeaderboardSort } from './domains/leaderboard';
import type { ServerRankingSort } from './types';

/**
 * Query-key factory shared by both SPAs so cache keys can never drift between
 * them. One entry per API domain; every domain hook keys off this factory.
 */
export const queryKeys = {
  servers: {
    all: ['servers'] as const,
    list: () => [...queryKeys.servers.all, 'list'] as const,
    detail: (id: number, page = 1, sort: ServerRankingSort = 'points') =>
      [...queryKeys.servers.all, 'detail', id, page, sort] as const,
  },
  games: {
    all: ['games'] as const,
    list: (params: { type?: string; page?: number; all?: boolean }) =>
      [
        ...queryKeys.games.all,
        'list',
        params.type ?? 'all',
        params.all === true ? 'every-status' : 'ended',
        params.page ?? 1,
      ] as const,
    detail: (id: number) => [...queryKeys.games.all, 'detail', id] as const,
  },
  leaderboard: {
    all: ['leaderboard'] as const,
    top100: (params: { season?: number; server?: number; sort?: LeaderboardSort; page?: number }) =>
      [
        ...queryKeys.leaderboard.all,
        'top100',
        params.season ?? 'all-time',
        params.server ?? 'all',
        params.sort ?? 'points',
        params.page ?? 1,
      ] as const,
  },
  seasons: {
    all: ['seasons'] as const,
    list: (page: number) => [...queryKeys.seasons.all, 'list', page] as const,
    detail: (num: number) => [...queryKeys.seasons.all, 'detail', num] as const,
  },
  players: {
    all: ['players'] as const,
    detail: (name: string) => [...queryKeys.players.all, 'detail', name] as const,
  },
  live: {
    all: ['live'] as const,
    servers: () => [...queryKeys.live.all, 'servers'] as const,
    game: (id: number) => [...queryKeys.live.all, 'game', id] as const,
  },
  discord: {
    all: ['discord'] as const,
    chat: () => [...queryKeys.discord.all, 'chat'] as const,
  },
  site: {
    all: ['site'] as const,
    settings: () => [...queryKeys.site.all, 'settings'] as const,
  },
  ip: {
    all: ['ip'] as const,
    lookup: (ip: string) => [...queryKeys.ip.all, 'lookup', ip] as const,
  },
  account: {
    all: ['account'] as const,
    gameLoginToken: () => [...queryKeys.account.all, 'game-login-token'] as const,
  },
  auth: {
    me: ['auth', 'me'] as const,
  },
  admin: {
    all: ['admin'] as const,
    users: {
      all: ['admin', 'users'] as const,
      list: (params: { search?: string; page?: number }) =>
        [...queryKeys.admin.users.all, 'list', params.search ?? '', params.page ?? 1] as const,
      detail: (id: number) => [...queryKeys.admin.users.all, 'detail', id] as const,
    },
    moderation: {
      all: ['admin', 'moderation'] as const,
      bans: () => [...queryKeys.admin.moderation.all, 'bans'] as const,
      history: (page: number) => [...queryKeys.admin.moderation.all, 'history', page] as const,
      player: (id: number) => [...queryKeys.admin.moderation.all, 'player', id] as const,
      players: (search: string) => [...queryKeys.admin.moderation.all, 'players', search] as const,
    },
    settings: {
      all: ['admin', 'settings'] as const,
      general: () => [...queryKeys.admin.settings.all, 'general'] as const,
    },
    achievements: () => [...queryKeys.admin.all, 'achievements'] as const,
    dashboard: () => [...queryKeys.admin.all, 'dashboard'] as const,
    fleet: {
      all: ['admin', 'fleet'] as const,
      list: () => [...queryKeys.admin.fleet.all, 'list'] as const,
      config: (id: string) => [...queryKeys.admin.fleet.all, 'config', id] as const,
      templates: () => [...queryKeys.admin.fleet.all, 'templates'] as const,
    },
  },
} as const;
